import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmailPreview } from '@/components/email/EmailPreview';

// Display-only preview of the medical-staff invite email. Mirrors AdminInviteEmailPage.
// The email body is the BE single-owner render (GET /admin/email-previews/staff-invite,
// from messages_ko) — the FE no longer builds its own template. The toolbar toggles the
// resend variant. `?resend=true` query starts the page in resend mode.
//
// PRD 03 §3.5 + customer decisions log 2026-05-22 §1 — this is the canonical
// `#/staff-invite-email` preview page (auth-protected, reached from the
// institution-managers screen's 📧 header button or the invite-panel preview link).

export function StaffInviteEmailPage() {
  const { t } = useTranslation('admin');
  const [searchParams] = useSearchParams();
  const [isResend, setIsResend] = useState(searchParams.get('resend') === 'true');

  // SHCT-651 — the 발신자/발송일시/받는사람 box is rendered ONCE, by the server
  // (`_layout.html :: page`). This page used to paint its own copy on top of that render, so the
  // header appeared twice. Recipient = the ?email= the invite was sent to, with a demo fallback;
  // date = now, formatted YYYY-MM-DD HH:mm — both handed to the server render as vars.
  const recipient =
    searchParams.get('email') ||
    t('medicalStaffManagement.inviteEmailPreview.metaRecipientFallback');
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const sentAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

  return (
    <div className="admin-email-preview-page">
      <div className="admin-email-preview-page__back">
        <Link to="/medical-staff-management">
          {t('medicalStaffManagement.inviteEmailPreview.backToList')}
        </Link>
      </div>

      <div className="email-preview-toolbar">
        <span className="email-preview-toolbar__label">
          {t('medicalStaffManagement.inviteEmailPreview.toolbarLabel')}
        </span>
        <div className="email-preview-toolbar__seg">
          <button
            type="button"
            className={isResend ? '' : 'is-active'}
            onClick={() => setIsResend(false)}
          >
            {t('medicalStaffManagement.inviteEmailPreview.modeNormal')}
          </button>
          <button
            type="button"
            className={isResend ? 'is-active' : ''}
            onClick={() => setIsResend(true)}
          >
            {t('medicalStaffManagement.inviteEmailPreview.modeResend')}
          </button>
        </div>
      </div>

      <EmailPreview
        kind="staff-invite"
        resend={isResend}
        vars={{ recipientEmail: recipient, sentAt }}
      />
    </div>
  );
}
